"use client";

import { Fragment, useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowLeft } from "@/components/icons";
import { AiSummary, AuthorAvatar, BlogCta, HeroGlow, ShareButton } from "@/components/ui/";
import CodeBlock from "@/components/ui/code-block";
import { getCategoryColor } from "./data";
import { PATH_BACK_LABELS } from "@/lib/constants";
import PostSidebar, { type PostTocItem } from "./post-sidebar";
import PostFaq from "./post-faq";
import ProductCta, { type CtaProduct } from "./product-cta";

/** An editor-placed paragraph that is only `[[cta]]` or `[[cta:mobile]]` (also
 *  `residential`, `chooser`) renders the inline product card. */
const CTA_MARKER = /^\[\[cta(?::(residential|mobile|chooser))?\]\]$/i;

function isPipeTable(text: string): boolean {
  const lines = text
    .trim()
    .split("\n")
    .filter((l) => l.trim());
  if (lines.length < 2) return false;
  return lines.filter((l) => l.includes("|")).length >= Math.ceil(lines.length * 0.8);
}

function parseRow(line: string): string[] {
  const parts = line.split("|").map((c) => c.trim());
  if (parts[0] === "") parts.shift();
  if (parts[parts.length - 1] === "") parts.pop();
  return parts;
}

const TABLE_INLINE_PATTERN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

/** Preformatted table cells are raw pasted text, not Prismic rich-text spans, so
 *  `**bold**` and `[label](url)` markers never go through `applySpans` — parse
 *  them here instead. */
function renderInlineMarkdown(text: string, nofollowSet?: Set<string>): React.ReactNode {
  if (!text.includes("**") && !text.includes("](")) return text;
  const parts = text.split(TABLE_INLINE_PATTERN).filter(Boolean);
  let offset = 0;
  return parts.map((part) => {
    const start = offset;
    offset += part.length;

    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={start} className="text-foreground font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = !href.includes("proxidize.com") && !href.startsWith("/");
      const isNofollow = nofollowSet?.has(href);
      const relParts = [
        "noopener",
        ...(isExternal ? ["noreferrer"] : []),
        ...(isNofollow ? ["nofollow"] : []),
      ];
      return (
        <a
          key={start}
          href={href}
          className="text-foreground underline underline-offset-2 hover:opacity-80"
          target={isExternal ? "_blank" : undefined}
          rel={relParts.join(" ")}
        >
          {label}
        </a>
      );
    }

    return <span key={start}>{part}</span>;
  });
}

function ProseTable({ text, nofollowSet }: { text: string; nofollowSet?: Set<string> }) {
  const lines = text
    .trim()
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^[\s|:-]+$/.test(l));
  const [headerLine, ...dataLines] = lines;
  const headers = parseRow(headerLine);
  const rows = dataLines.map(parseRow);

  return (
    <div className="border-border my-6 overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border bg-card border-b">
            {headers.map((h) => (
              <th
                key={h}
                className="text-foreground px-4 py-3 text-left text-xs font-semibold tracking-wide whitespace-nowrap uppercase"
              >
                {renderInlineMarkdown(h, nofollowSet)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&>tr:nth-child(even)]:bg-surface-2">
          {rows.map((row) => (
            <tr key={row.join("|")} className="border-border/50 border-b last:border-0">
              {row.map((cell) => (
                <td key={cell} className="text-muted-foreground px-4 py-3 leading-relaxed">
                  {renderInlineMarkdown(cell, nofollowSet)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type RichTextSpan = { type: string; start: number; end: number; data?: { url?: string } };
type RichTextNode = {
  type: string;
  text?: string;
  spans?: RichTextSpan[];
  direction?: string;
  url?: string;
  alt?: string;
  dimensions?: { width: number; height: number };
  id?: string;
};
type ContentBlock = {
  body_content: RichTextNode[];
  image:
    | { url?: string; alt?: string; dimensions?: { width: number; height: number } }
    | Record<string, never>;
  caption: string | null;
  nofollow_urls: string | null;
  code_language?: string | null;
};
type FAQ = { question: string | null; answer: RichTextNode[] };

function applySpans(
  text: string,
  spans: RichTextSpan[] | undefined,
  nofollowSet?: Set<string>
): string {
  if (!spans?.length) return escapeHtml(text);
  const sorted = [...spans].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start)
  );
  const processed = new Set<number>();
  let result = "";
  let cursor = 0;

  for (let i = 0; i < sorted.length; i++) {
    if (processed.has(i)) continue;
    const span = sorted[i];
    if (span.start < cursor) {
      processed.add(i);
      continue;
    }

    result += escapeHtml(text.slice(cursor, span.start));

    const nestedSpans: RichTextSpan[] = [];
    for (let j = i + 1; j < sorted.length; j++) {
      const s = sorted[j];
      if (s.start >= span.start && s.end <= span.end) {
        nestedSpans.push({ ...s, start: s.start - span.start, end: s.end - span.start });
        processed.add(j);
      }
    }

    const innerText = text.slice(span.start, span.end);
    const inner = nestedSpans.length
      ? applySpans(innerText, nestedSpans, nofollowSet)
      : escapeHtml(innerText);

    if (span.type === "strong") result += `<strong>${inner}</strong>`;
    else if (span.type === "em") result += `<em>${inner}</em>`;
    else if (span.type === "hyperlink" && span.data?.url) {
      const isExternal = !span.data.url.includes("proxidize.com") && !span.data.url.startsWith("/");
      const isNofollow = nofollowSet?.has(span.data.url);
      const relParts = [
        "noopener",
        ...(isExternal ? ["noreferrer"] : []),
        ...(isNofollow ? ["nofollow"] : []),
      ];
      const targetAttr = isExternal ? ` target="_blank"` : "";
      result += `<a href="${span.data.url}" class="text-foreground underline underline-offset-2 hover:opacity-80"${targetAttr} rel="${relParts.join(" ")}">${inner}</a>`;
    } else {
      result += inner;
    }

    cursor = span.end;
  }

  result += escapeHtml(text.slice(cursor));
  return result;
}

function escapeHtml(str: string) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Anchor id for a heading — also used by the sidebar's "Jump to" list. */
function slugify(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

type RichGroup =
  | { type: "ul"; items: RichTextNode[] }
  | { type: "ol"; items: RichTextNode[] }
  | { type: "node"; node: RichTextNode };

function groupNodes(nodes: RichTextNode[]): RichGroup[] {
  const groups: RichGroup[] = [];
  for (const node of nodes) {
    const last = groups[groups.length - 1];
    if (node.type === "list-item") {
      if (last?.type === "ul") {
        last.items.push(node);
      } else {
        groups.push({ type: "ul", items: [node] });
      }
    } else if (node.type === "o-list-item") {
      if (last?.type === "ol") {
        last.items.push(node);
      } else {
        groups.push({ type: "ol", items: [node] });
      }
    } else {
      groups.push({ type: "node", node });
    }
  }
  return groups;
}

interface InlineCtaConfig {
  defaultProduct: CtaProduct | null;
  postSlug: string;
}

function RichText({
  nodes,
  nofollowSet,
  codeLang,
  inlineCta,
}: {
  nodes: RichTextNode[];
  nofollowSet?: Set<string>;
  codeLang?: string | null;
  inlineCta?: InlineCtaConfig;
}) {
  if (!nodes?.length) return null;
  const groups = groupNodes(nodes);
  return (
    <div className="space-y-4">
      {groups.map((group) => {
        if (group.type === "ul") {
          return (
            <ul
              key={`ul-${group.items[0]?.text?.slice(0, 30) || "empty"}`}
              className="list-outside list-disc space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const text = node.text ?? "";
                const html = applySpans(text, node.spans, nofollowSet);
                const dir = node.direction === "rtl" ? "rtl" : undefined;
                return (
                  <li
                    key={`${node.type}-${text.slice(0, 40)}`}
                    dir={dir}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ul>
          );
        }
        if (group.type === "ol") {
          return (
            <ol
              key={`ol-${group.items[0]?.text?.slice(0, 30) || "empty"}`}
              className="list-outside list-decimal space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const text = node.text ?? "";
                const html = applySpans(text, node.spans, nofollowSet);
                const dir = node.direction === "rtl" ? "rtl" : undefined;
                return (
                  <li
                    key={`${node.type}-${text.slice(0, 40)}`}
                    dir={dir}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ol>
          );
        }
        const { node } = group;
        if (node.type === "image") {
          if (!node.url) return null;
          return (
            <figure key={`inline-img-${node.id ?? node.url}`} className="my-6">
              <div className="overflow-hidden rounded-xl border border-white/[0.06]">
                <Image
                  src={node.url}
                  alt={node.alt ?? ""}
                  width={node.dimensions?.width ?? 1200}
                  height={node.dimensions?.height ?? 630}
                  className="h-auto w-full object-cover"
                />
              </div>
            </figure>
          );
        }
        const text = node.text ?? "";
        const html = applySpans(text, node.spans, nofollowSet);
        const dir = node.direction === "rtl" ? "rtl" : undefined;
        const nodeKey = `${node.type}-${text.slice(0, 40)}`;
        const marker = node.type === "paragraph" ? CTA_MARKER.exec(text.trim()) : null;
        if (marker) {
          const product =
            (marker[1]?.toLowerCase() as CtaProduct | undefined) ?? inlineCta?.defaultProduct;
          if (!inlineCta || !product) return null;
          return (
            <ProductCta
              key={nodeKey}
              product={product}
              placement="inline"
              postSlug={inlineCta.postSlug}
            />
          );
        }
        switch (node.type) {
          case "heading1":
            // Downshifted to h2: the page title is the sole h1, so body headings start at h2.
            return (
              <h2
                key={nodeKey}
                id={slugify(text)}
                dir={dir}
                className="3xl:text-4xl text-foreground mt-10 mb-4 scroll-mt-24 text-3xl font-black"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading2":
            return (
              <h2
                key={nodeKey}
                id={slugify(text)}
                dir={dir}
                className="3xl:text-3xl text-foreground mt-10 mb-4 scroll-mt-24 text-2xl font-bold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading3":
            return (
              <h3
                key={nodeKey}
                id={slugify(text)}
                dir={dir}
                className="text-foreground mt-8 mb-3 scroll-mt-24 text-lg font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading4":
            return (
              <h4
                key={nodeKey}
                dir={dir}
                className="3xl:text-xl text-foreground mt-6 mb-2 text-lg font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading5":
          case "heading6":
            return (
              <h5
                key={nodeKey}
                dir={dir}
                className="3xl:text-lg text-foreground mt-6 mb-2 text-base font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "preformatted":
            if (isPipeTable(text))
              return <ProseTable key={nodeKey} text={text} nofollowSet={nofollowSet} />;
            return <CodeBlock key={nodeKey} code={text} lang={codeLang ?? undefined} />;
          default:
            return text ? (
              <p
                key={nodeKey}
                dir={dir}
                className="text-muted-foreground 3xl:text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : null;
        }
      })}
    </div>
  );
}

interface PrismicBlogPostProps {
  title: string;
  description: string;
  category: string;
  date: string;
  updatedDate?: string | null;
  author: string;
  authorUid: string | null;
  authorImage: string | null;
  featuredImage: string | null;
  contentBlocks: ContentBlock[];
  faqs: FAQ[];
  pageUrl: string;
  commercialIntent?: string | null;
  recommendedProduct?: string | null;
}

const CTA_PRODUCTS: ReadonlyArray<string> = ["residential", "mobile", "chooser"];

export function PrismicBlogPost({
  title,
  description,
  category,
  date,
  updatedDate,
  author,
  authorUid,
  authorImage,
  featuredImage,
  contentBlocks,
  faqs,
  pageUrl,
  commercialIntent,
  recommendedProduct,
}: PrismicBlogPostProps) {
  const router = useRouter();
  const prefersReducedMotion = useReducedMotion();
  const color = getCategoryColor(category);
  const postSlug = pageUrl.split("/").filter(Boolean).pop() ?? "";
  // Pricing CTAs are only for posts tagged medium/high intent with a product;
  // everything else keeps the generic footer CTA.
  const ctaProduct: CtaProduct | null =
    (commercialIntent === "high" || commercialIntent === "medium") &&
    recommendedProduct &&
    CTA_PRODUCTS.includes(recommendedProduct)
      ? (recommendedProduct as CtaProduct)
      : null;

  const [backLabel, setBackLabel] = useState("Back to blog");
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    const prev = sessionStorage.getItem("prevPath");
    if (!prev) return;
    const path = prev.replace(/\/$/, "") || "/";
    const resolved = PATH_BACK_LABELS[path];
    if (resolved) setBackLabel(resolved);
    setCanGoBack(true);
  }, []);

  /* "Jump to" items — the top-level headings of the article, matching the ids
     RichText stamps on them. */
  const toc: PostTocItem[] = contentBlocks.flatMap((block) =>
    (block.body_content ?? [])
      .filter((n) => (n.type === "heading1" || n.type === "heading2") && n.text?.trim())
      .map((n) => ({ id: slugify(n.text ?? ""), label: n.text ?? "" }))
  );

  return (
    <main id="main-content" tabIndex={-1} className="relative isolate overflow-y-clip">
      {/* The page-hero ribbon. These article templates have no hero section, so
          HeroGlow bounds itself to a band at the top of the document and lands
          where it does on every other page. The main must NOT clip the X axis —
          the ribbon is 100vw and has to bleed past the shell's max-w-7xl frame. */}
      <HeroGlow align="left" behind />
      {/* Same container as the site header, so content lines up with the logo */}
      <div className="relative mx-auto max-w-7xl px-4 pt-24 pb-28 sm:px-6 lg:px-8">
        {/* ── Header: back link, date, title, subtitle ─────────────────── */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            type="button"
            onClick={() => (canGoBack ? router.back() : router.push("/blogs"))}
            className="text-muted-foreground hover:text-foreground mb-10 inline-flex cursor-pointer items-center gap-1.5 text-sm transition-colors"
          >
            <ArrowLeft size={14} />
            {backLabel}
          </button>

          {/* Header follows the same two-column shape as the body, so the
              action buttons line up with the table of contents below. */}
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:gap-14 xl:gap-20">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <span className="bg-foreground text-background inline-flex rounded-full px-3 py-1 text-[11px] font-semibold">
                  {category}
                </span>
                <p className="text-muted-foreground text-[13px]">
                  Published {date}
                  {updatedDate && ` · Updated ${updatedDate}`}
                </p>
              </div>

              <h1 className="text-foreground mt-4 max-w-4xl text-3xl leading-[1.15] font-bold tracking-tight sm:text-4xl lg:text-[44px]">
                {title}
              </h1>

              {description && (
                <p className="text-muted-foreground mt-5 max-w-3xl text-lg leading-relaxed">
                  {description}
                </p>
              )}

              <div className="mt-6 flex items-center gap-2.5">
                {authorImage ? (
                  <Image
                    src={authorImage}
                    alt={author}
                    width={28}
                    height={28}
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <AuthorAvatar
                    initial={author[0]?.toUpperCase() ?? "P"}
                    color={color}
                    className="h-7 w-7 text-xs"
                  />
                )}
                {authorUid ? (
                  <Link
                    href={`/author/${authorUid}`}
                    className="text-foreground text-sm font-medium underline-offset-2 hover:underline"
                  >
                    {author}
                  </Link>
                ) : (
                  <p className="text-foreground text-sm font-medium">{author}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:w-64 lg:shrink-0">
              <AiSummary mdPath={`/api/md/blog/${pageUrl.split("/").filter(Boolean).pop()}`} />
              <ShareButton title={title} />
            </div>
          </div>
        </motion.div>

        {/* ── Two columns: article left, sticky rail right ─────────────── */}
        <div className="mt-12 flex items-start gap-14 xl:gap-20">
          <motion.article
            id="post-article"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="min-w-0 flex-1"
          >
            {featuredImage && (
              <figure className="border-border bg-card mb-12 overflow-hidden rounded-2xl border">
                <Image
                  src={featuredImage}
                  alt={title}
                  width={1200}
                  height={630}
                  sizes="(min-width: 1024px) 900px, 100vw"
                  className="h-auto w-full object-cover"
                  priority
                />
              </figure>
            )}

            <div className="space-y-8">
              {contentBlocks.map((block, i) => {
                const hasText = block.body_content?.some((n) => n.text?.trim());
                const imgUrl = (block.image as { url?: string })?.url;
                const nofollowSet = block.nofollow_urls
                  ? new Set(
                      block.nofollow_urls
                        .split("\n")
                        .map((u) => u.trim())
                        .filter(Boolean)
                    )
                  : undefined;
                const blockKey =
                  block.body_content?.[0]?.text?.slice(0, 30) ??
                  (imgUrl ? `img-${i}` : `block-empty-${i}`);

                return (
                  <Fragment key={blockKey}>
                    <div>
                      {hasText && (
                        <RichText
                          nodes={block.body_content}
                          nofollowSet={nofollowSet}
                          codeLang={block.code_language}
                          inlineCta={{ defaultProduct: ctaProduct, postSlug }}
                        />
                      )}
                      {imgUrl && (
                        <figure className="my-6">
                          <div className="border-foreground/6 overflow-hidden rounded-xl border">
                            <Image
                              src={imgUrl}
                              alt={(block.image as { alt?: string }).alt ?? block.caption ?? ""}
                              width={1200}
                              height={630}
                              className="h-auto w-full object-cover"
                              style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
                            />
                          </div>
                          {block.caption && (
                            <figcaption className="text-muted-foreground mt-2 text-center text-xs">
                              {block.caption}
                            </figcaption>
                          )}
                        </figure>
                      )}
                    </div>
                    {i === 0 && ctaProduct && (
                      <ProductCta
                        product={ctaProduct}
                        placement="after_summary"
                        postSlug={postSlug}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>

            {/* ── FAQs, directly under the post content ───────────────── */}
            {faqs.length > 0 && (
              <PostFaq
                faqs={faqs.map((faq) => ({
                  q: faq.question ?? "",
                  a: <RichText nodes={faq.answer} />,
                }))}
              />
            )}
          </motion.article>

          <PostSidebar toc={toc} />
        </div>

        {ctaProduct ? (
          <ProductCta product={ctaProduct} placement="end_of_post" postSlug={postSlug} />
        ) : (
          <BlogCta />
        )}
      </div>
    </main>
  );
}
